import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ujmsRequestWarrant, ujmsServeWarrant, ujmsRecallWarrant, ujmsScheduleHearing, ujmsSearchCases } from "../../../services/ujmsApi";
import { Field, inputCls, selectCls, btnPrimary, btnGhost, btnDanger } from "../../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";

export function nextBusinessDay() {
  const d = new Date();
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  return d.toISOString().slice(0, 10);
}

export function RequestDialog({ open, onClose, onDone, canIssue, presetCase }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const blank = { warrant_type: "BENCH", notes: "", bond_amount: "", issue_now: false, case: presetCase || null };
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF({ ...blank, case: presetCase || null }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const search = async () => { try { const r = await ujmsSearchCases({ q, page_size: 6 }); setResults(r.data.items); } catch (e) { /* non-blocking */ } };
  const submit = async () => {
    try {
      const payload = { warrant_type: f.warrant_type, notes: f.notes, issue_now: f.issue_now };
      if (f.bond_amount) payload.bond_amount = parseFloat(f.bond_amount);
      const r = await ujmsRequestWarrant(f.case.id, payload);
      toast.success(f.issue_now && canIssue ? "Warrant issued" : "Warrant requested — awaiting judicial review");
      if (r.data.return_hearing) toast.success(`Return hearing auto-set for ${r.data.return_hearing.date} at 09:00`);
      onClose(); onDone(r.data); setResults([]); setQ("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="wr-request-dialog">
        <DialogHeader><DialogTitle>Request warrant</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Case" required>
            <div className="flex gap-2">
              <input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case number or party…" data-testid="wr-case-search" onKeyDown={(e) => e.key === "Enter" && search()} />
              <button className={btnGhost} onClick={search} data-testid="wr-case-search-btn">Search</button>
            </div>
          </Field>
          {!f.case && results.length > 0 && (
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-36 overflow-y-auto">
              {results.map((c) => (
                <button key={c.id} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50" data-testid={`wr-case-${c.case_number}`} onClick={() => setF({ ...f, case: c })}>
                  <span className="font-mono text-xs text-blue-900">{c.case_number}</span> {c.caption}
                </button>
              ))}
            </div>
          )}
          {f.case && (
            <div className="text-sm bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid="wr-case-selected">
              <span className="font-mono text-xs text-blue-900">{f.case.case_number}</span> — {f.case.caption}
              <button className="ml-2 text-xs text-slate-400 underline" onClick={() => setF({ ...f, case: null })}>change</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type" required>
              <select className={selectCls} value={f.warrant_type} onChange={(e) => setF({ ...f, warrant_type: e.target.value })} data-testid="wr-type">{["BENCH", "ARREST", "SEARCH"].map((t) => <option key={t}>{t}</option>)}</select>
            </Field>
            <Field label="Bond ($)"><input type="number" className={inputCls} value={f.bond_amount} onChange={(e) => setF({ ...f, bond_amount: e.target.value })} data-testid="wr-bond" /></Field>
          </div>
          <Field label="Grounds / notes"><input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Failure to appear at…" data-testid="wr-notes" /></Field>
          {canIssue && (
            <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={f.issue_now} onChange={(e) => setF({ ...f, issue_now: e.target.checked })} data-testid="wr-issue-now" /> Issue immediately (judicial authority)
            </label>
          )}
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={!f.case} data-testid="wr-request-submit">{f.issue_now && canIssue ? "Issue warrant" : "Submit request"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ServeDialog({ w, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try { await ujmsServeWarrant(w.id, notes); toast.success("Warrant served — docketed"); onClose(); onDone(); setNotes(""); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="wr-serve-dialog">
        <DialogHeader><DialogTitle>Serve warrant — {w?.person_name || w?.case_number}</DialogTitle></DialogHeader>
        <Field label="Service notes"><input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Served at…" data-testid="wr-serve-notes" /></Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} data-testid="wr-serve-submit">Mark served</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RecallDialog({ w, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { await ujmsRecallWarrant(w.id, reason); toast.success("Warrant recalled"); onClose(); onDone(); setReason(""); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="wr-recall-dialog">
        <DialogHeader><DialogTitle>Recall warrant — {w?.person_name || w?.case_number}</DialogTitle></DialogHeader>
        <Field label="Reason" required><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Defendant appeared voluntarily…" data-testid="wr-recall-reason" /></Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnDanger} onClick={submit} disabled={!reason.trim()} data-testid="wr-recall-submit">Recall</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SetHearingDialog({ w, onClose, onDone }) {
  const [f, setF] = useState({ date: "", start_time: "09:00" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (w) setF({ date: nextBusinessDay(), start_time: "09:00" }); }, [w]);
  const submit = async () => {
    setBusy(true);
    const base = { hearing_type: "WARRANT_HEARING", date: f.date, start_time: f.start_time, duration_minutes: 30, notes: `Warrant return — ${w.warrant_type} warrant for ${w.person_name || "defendant"}` };
    try {
      await ujmsScheduleHearing(w.case_id, base);
      toast.success(`Warrant hearing set for ${f.date} at ${f.start_time} — parties noticed`); onClose(); onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.conflicts) {
        try {
          await ujmsScheduleHearing(w.case_id, { ...base, override: true, override_reason: `Warrant return priority setting (${detail.conflicts.map((c) => c.type).join(", ")})` });
          toast.success(`Warrant hearing set for ${f.date} at ${f.start_time} — calendar conflict overridden and docketed`); onClose(); onDone();
        } catch (e2) { toast.error(e2.response?.data?.detail || "Failed to schedule"); }
      } else { toast.error(typeof detail === "string" ? detail : "Failed to schedule"); }
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="wr-set-hearing-dialog">
        <DialogHeader><DialogTitle>Set warrant hearing — {w?.person_name || w?.case_number}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Case <span className="font-mono text-blue-900">{w?.case_number}</span> · {w?.warrant_type} warrant · set on the case's judge and courtroom; parties are noticed automatically.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date" required><input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="wr-hearing-date" /></Field>
          <Field label="Time" required><input type="time" className={inputCls} value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="wr-hearing-time" /></Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!f.date || busy} onClick={submit} data-testid="wr-set-hearing-submit">{busy ? "Scheduling…" : "Schedule warrant hearing"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
